import { API_BASE_URL } from './apiClient'

// Every lead form on the site (/contact, /hire-an-individual, and the form on
// the service, industry and owner pages) sends through submitLead().
//
// The GHL Prime backend stores the lead first, then sends it to each webhook
// set up in Admin -> Lead Webhooks (the GoHighLevel webhooks, PRISM, and any
// added later), retrying failed sends. The form's payload goes to every
// webhook unchanged.
//
// If the backend can't be reached, the lead goes straight from the browser to
// the form's own GoHighLevel webhook, as the forms worked before, and waits in
// this browser's outbox; flushLeadOutbox() hands it to the backend on a later
// page load, so it is still stored and sent to the other webhooks.

const OUTBOX_KEY = 'ghlp_lead_outbox'
const TIMEOUT_MS = 10000
const MAX_OUTBOX_AGE_MS = 30 * 864e5

function newRef() {
  try {
    return crypto.randomUUID()
  } catch {
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`
  }
}

async function postJson(url, body) {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS)
  try {
    // keepalive lets the request finish even if the page navigates away.
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: controller.signal,
      keepalive: true,
    })
    return { ok: res.ok, status: res.status }
  } catch {
    return { ok: false, status: 0 }
  } finally {
    clearTimeout(timer)
  }
}

// Network errors, timeouts, rate limits and server errors are worth retrying
// later; a 4xx means the backend rejected the lead itself.
const retryable = (status) => status === 0 || status === 429 || status >= 500

function readOutbox() {
  try {
    const items = JSON.parse(localStorage.getItem(OUTBOX_KEY) || '[]')
    return Array.isArray(items) ? items : []
  } catch {
    return []
  }
}

function writeOutbox(items) {
  try {
    if (items.length) localStorage.setItem(OUTBOX_KEY, JSON.stringify(items))
    else localStorage.removeItem(OUTBOX_KEY)
  } catch {
    // Storage blocked: the lead still reached GoHighLevel directly.
  }
}

/**
 * @param {{ form: 'contact' | 'hire' | 'service', payload: object, fallbackWebhookUrl: string }} lead
 * @returns {Promise<{ ok: boolean }>} ok when the lead was stored or reached GoHighLevel
 */
export async function submitLead({ form, payload, fallbackWebhookUrl }) {
  const record = { form, client_ref: newRef(), payload }
  const stored = await postJson(`${API_BASE_URL}/api/leads/submit`, record)
  if (stored.ok) return { ok: true }

  const direct = fallbackWebhookUrl ? await postJson(fallbackWebhookUrl, payload) : { ok: false }
  if (retryable(stored.status)) {
    writeOutbox([
      ...readOutbox(),
      { ...record, delivered_urls: direct.ok ? [fallbackWebhookUrl] : [], queued_at: Date.now() },
    ])
  }
  return { ok: direct.ok }
}

/** Hands queued leads to the backend. Runs once per page load (App.jsx). */
export async function flushLeadOutbox() {
  const items = readOutbox()
  if (!items.length) return
  const keep = []
  for (const item of items) {
    if (Date.now() - (item.queued_at || 0) > MAX_OUTBOX_AGE_MS) continue
    const { queued_at: queuedAt, ...record } = item
    const res = await postJson(`${API_BASE_URL}/api/leads/submit`, record)
    if (!res.ok && retryable(res.status)) keep.push({ ...record, queued_at: queuedAt })
  }
  writeOutbox(keep)
}
